export const ADMIN_PERMISSIONS = Object.freeze({
  ORDERS: 'orders.access',
  CONTRACTORS: 'contractors.access',
  COMMUNICATIONS: 'communications.access',
  CUSTOMERS: 'customers.access',
  SUBJECTS: 'subjects.access',
  SERVICES: 'services.access',
  PARTNERS: 'partners.access',
  SETTINGS: 'settings.manage',
  ADMINS: 'admins.manage',
  ANALYTICS: 'analytics.view',
  MONITORING: 'monitoring.view',
  DATABASE: 'database.manage',
});

export const hasAccessV2 = (user) => !!(
  user
  && (
    user.accessVersion === 2
    || typeof user.role === 'string'
    || Array.isArray(user.accessPermissions)
    || Array.isArray(user.effectivePermissions)
  )
);

export const isSuperAdmin = (user) => hasAccessV2(user) && user.role === 'super_admin';

export const canAccess = (user, permission) => {
  if (!user) {
    return false;
  }

  // Deployment safety: an authenticated user returned by an older backend does not
  // know Access V2. In that short compatibility window keep the historical frontend
  // behavior instead of hiding everything. Authentication itself never fails open.
  if (!hasAccessV2(user)) {
    return true;
  }

  if (isSuperAdmin(user)) {
    return true;
  }

  let permissions = [];
  if (Array.isArray(user.effectivePermissions)) {
    permissions = user.effectivePermissions;
  } else if (Array.isArray(user.accessPermissions)) {
    permissions = user.accessPermissions;
  }

  return permissions.includes(permission);
};

export const canAccessAny = (user, permissions) => permissions.some(
  (permission) => canAccess(user, permission),
);

export const firstAllowedRoute = (user) => {
  const candidates = [
    [ADMIN_PERMISSIONS.ORDERS, 'orders'],
    [ADMIN_PERMISSIONS.CONTRACTORS, 'contractors'],
    [ADMIN_PERMISSIONS.COMMUNICATIONS, 'communications'],
    [ADMIN_PERMISSIONS.SERVICES, 'services'],
    [ADMIN_PERMISSIONS.CUSTOMERS, 'customers'],
    [ADMIN_PERMISSIONS.SUBJECTS, 'objects'],
    [ADMIN_PERMISSIONS.PARTNERS, 'partners'],
    [ADMIN_PERMISSIONS.SETTINGS, 'settings.limit'],
    [ADMIN_PERMISSIONS.ADMINS, 'settings.admins'],
    [ADMIN_PERMISSIONS.ANALYTICS, 'settings.analytics'],
    [ADMIN_PERMISSIONS.MONITORING, 'settings.monitoring'],
    [ADMIN_PERMISSIONS.DATABASE, 'settings.db'],
  ];

  const match = candidates.find(([permission]) => canAccess(user, permission));
  return match ? match[1] : 'login';
};

export const legacyPermissionsFromAccess = (permissions = []) => ({
  editOrders: permissions.includes(ADMIN_PERMISSIONS.ORDERS),
  editContractors: permissions.includes(ADMIN_PERMISSIONS.CONTRACTORS),
  editCustomers: permissions.includes(ADMIN_PERMISSIONS.CUSTOMERS),
  editSubjects: permissions.includes(ADMIN_PERMISSIONS.SUBJECTS),
  editSettings: permissions.includes(ADMIN_PERMISSIONS.SETTINGS),
  editAdmins: permissions.includes(ADMIN_PERMISSIONS.ADMINS),
});
